
import { useState, useEffect, useCallback } from 'react';
import { Technique } from '@/types/technique';
import type { TechniqueOffline } from '@/services/offline/indexeddb/schema';
import { getUserSavedTechniques, saveTechnique, removeTechnique } from '@/services/userTechniqueService';
import { usePermissions } from '@/contexts/unified-permissions';

export const useSavedTechniques = (techniques: (Technique | TechniqueOffline)[]) => {
  const { isAuthenticated, user } = usePermissions();
  const [savedTechniques, setSavedTechniques] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Create a stable reference for technique IDs
  const techniqueIds = techniques.map(t => t.id).sort().join(',');

  useEffect(() => {
    if (!isAuthenticated || !user?.id || techniques.length === 0) {
      setSavedTechniques([]);
      return;
    }

    const fetchSavedTechniques = async () => {
      setIsLoading(true);
      try {
        const saved = await getUserSavedTechniques(user.id);
        const savedIds = saved.map(st => st.technique?.id).filter(Boolean) as string[];
        setSavedTechniques(savedIds);
      } catch (error) {
        console.error('Error fetching saved techniques:', error);
        setSavedTechniques([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSavedTechniques();
  }, [isAuthenticated, user?.id, techniqueIds, techniques.length]); // Use stable techniqueIds reference

  const handleSaveTechnique = useCallback(async (technique: Technique | TechniqueOffline) => {
    if (!isAuthenticated || !user?.id) return;

    const isCurrentlySaved = savedTechniques.includes(technique.id);
    
    try {
      if (isCurrentlySaved) {
        await removeTechnique(technique.id);
        setSavedTechniques(prev => prev.filter(id => id !== technique.id));
      } else {
        await saveTechnique(technique.id);
        setSavedTechniques(prev => [...prev, technique.id]);
      }
    } catch (error) {
      console.error('Error toggling technique save status:', error);
    }
  }, [isAuthenticated, user?.id, savedTechniques]);

  return {
    savedTechniques,
    handleSaveTechnique,
    isLoading
  };
};
